import React, { useContext, useState } from 'react';
import { DiscordContext } from './DiscordContext';
import './discord-boot-panel.css';

/**
 * Discord Activity cold-start status panel.
 *
 * Surfaces every leg of the embedded-mount handshake
 * (embeddedDetect → sdkReady → authorize → csrfPrime → hshSession →
 * sdkAuthenticate → lobbyFetch) as a live row: name, status glyph,
 * elapsed ms when complete, and the error token if a leg failed.
 *
 * Renders nothing on open web (isEmbedded === false) — the panel is
 * a Discord-iframe diagnostic only. State is in-session only: collapse
 * / hide are local useState, never persisted.
 */

// Stage rows render in cold-start order. Keep this in sync with the
// bootStages keys in DiscordContext / DiscordProvider.
const STAGE_ORDER = [
    'embeddedDetect',
    'sdkReady',
    'authorize',
    'csrfPrime',
    'hshSession',
    'sdkAuthenticate',
    'lobbyFetch',
];

const GLYPH_OK = '✓';       // ✓
const GLYPH_FAILED = '✗';   // ✗
const GLYPH_PENDING = '⋯';  // ⋯

const PLACEHOLDER = '—';    // — (em dash, for empty id fields)


function lastSix(value) {
    if (!value) {
        return PLACEHOLDER;
    }
    const text = String(value);
    return text.length > 6 ? '…' + text.slice(-6) : text;
}


function stageDurationMs(stage) {
    if (stage && stage.startedAt != null && stage.completedAt != null) {
        return stage.completedAt - stage.startedAt;
    }
    return null;
}


function StageRow({ name, stage }) {
    const status = (stage && stage.status) || 'pending';
    const duration = stageDurationMs(stage);

    let glyph = GLYPH_PENDING;
    let className =
        'discord-boot-panel-stage discord-boot-panel-stage-pending';
    if (status === 'ok') {
        glyph = GLYPH_OK;
        className = 'discord-boot-panel-stage discord-boot-panel-stage-ok';
    } else if (status === 'failed') {
        glyph = GLYPH_FAILED;
        className =
            'discord-boot-panel-stage discord-boot-panel-stage-failed';
    }

    let detail = '';
    if (status === 'failed' && stage && stage.error) {
        detail = stage.error;
    } else if (status === 'pending') {
        // A pending leg that has started reads "pending"; one that has
        // not started yet reads blank (matches the cold-start mockup).
        detail = stage && stage.startedAt != null ? 'pending' : '';
    } else if (duration != null) {
        detail = '(' + duration + 'ms)';
    }

    return (
        <div className={className} data-stage={name}>
            <span className="discord-boot-panel-glyph">{glyph}</span>
            <span className="discord-boot-panel-stage-name">{name}</span>
            <span className="discord-boot-panel-stage-detail">{detail}</span>
        </div>
    );
}


export function DiscordBootPanel() {
    const discord = useContext(DiscordContext);
    const [collapsed, setCollapsed] = useState(false);
    const [hidden, setHidden] = useState(false);

    // Open-web no-op: the panel is an iframe-only diagnostic.
    if (!discord || !discord.isEmbedded) {
        return null;
    }

    if (hidden) {
        return (
            <button
                type="button"
                className="discord-boot-panel-reveal"
                aria-label="reveal-panel"
                onClick={() => setHidden(false)}
            >D</button>
        );
    }

    const bootStages = discord.bootStages || {};
    const lobby = discord.lobby;

    return (
        <div className="discord-boot-panel">
            <div className="discord-boot-panel-header">
                <span className="discord-boot-panel-title">
                    DISCORD ACTIVITY
                </span>
                <span className="discord-boot-panel-toggles">
                    <button
                        type="button"
                        className="discord-boot-panel-toggle"
                        aria-label="toggle-stages"
                        onClick={() => setCollapsed((c) => !c)}
                    >_</button>
                    <button
                        type="button"
                        className="discord-boot-panel-toggle"
                        aria-label="hide-panel"
                        onClick={() => setHidden(true)}
                    >{'×'}</button>
                </span>
            </div>

            <div className="discord-boot-panel-meta">
                channel={lastSix(discord.channelId)}{'  '}
                guild={lastSix(discord.guildId)}{'  '}
                instance={lastSix(discord.instanceId)}
            </div>

            <div className="discord-boot-panel-identity">
                identity: @{discord.discordUsername || PLACEHOLDER}
                {'  ·  '}global: {discord.discordGlobalName || PLACEHOLDER}
                {'  ·  '}nick: {discord.discordNick || PLACEHOLDER}
            </div>

            {lobby
                ? (
                    <div className="discord-boot-panel-lobby">
                        lobby: {lobby.uuid
                            ? String(lobby.uuid).slice(0, 8)
                            : PLACEHOLDER}{'  '}
                        owner={String(discord.lobbyIsOwner)}{'  '}
                        seat={String(discord.lobbySeatIndex)}
                    </div>
                )
                : null}

            {collapsed
                ? null
                : (
                    <div className="discord-boot-panel-stages">
                        {STAGE_ORDER.map((name) => (
                            <StageRow
                                key={name}
                                name={name}
                                stage={bootStages[name]}
                            />
                        ))}
                    </div>
                )}
        </div>
    );
}
